import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Box, Grid, Card, Typography, CircularProgress, Alert,
  LinearProgress, Chip, Button,
} from "@mui/material";
import AppsOutlinedIcon from "@mui/icons-material/AppsOutlined";
import ExtensionOutlinedIcon from "@mui/icons-material/ExtensionOutlined";
import { API_BASE_URL } from "../../config";
import { useAuth } from "../../context/AuthContext";
import { canOnboard, isTenantAdmin } from "../../utils/entitlements";
import {
  contractDaysLeft,
  shouldShowContractExtensionBanner,
  contractExtensionBannerMessage,
} from "../../utils/contractExtension";

function KpiCard({ label, value, sub, color }) {
  return (
    <Card elevation={0} sx={{ border: "1px solid #e8e8e8", borderRadius: 2, p: 3, bgcolor: "#fff" }}>
      <Typography fontSize="13px" color="text.secondary" mb={0.5}>{label}</Typography>
      <Typography fontSize="28px" fontWeight={700} color={color || "#344054"}>{value ?? "—"}</Typography>
      {sub && <Typography fontSize="12px" color="text.secondary" mt={0.5}>{sub}</Typography>}
    </Card>
  );
}

export default function ClientOverviewPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const showOnboard = canOnboard(user?.roles);
  const showExtensionBanner = stats && shouldShowContractExtensionBanner(stats);
  const canRequestExtension = isTenantAdmin(user?.roles);

  useEffect(() => {
    axios.get(`${API_BASE_URL}/client/stats`)
      .then((res) => setStats(res.data))
      .catch((err) => {
        const msg = err.response?.data?.message || err.response?.data?.error || err.message;
        setError(msg ? `Failed to load overview data: ${msg}` : "Failed to load overview data.");
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
      <CircularProgress />
    </Box>
  );

  if (error) return (
    <Box sx={{ p: 4 }}><Alert severity="error">{error}</Alert></Box>
  );

  const usedPct = stats?.seat_count > 0
    ? Math.min(100, Math.round((stats.seats_used / stats.seat_count) * 100))
    : 0;
  const overWarn = usedPct >= (stats?.warning_threshold || 80);

  const daysLeft = contractDaysLeft(stats?.contract_end);
  const extensionSeverity =
    stats?.license_access === "HARD_LOCK" || (daysLeft !== null && daysLeft < 0)
      ? "error"
      : stats?.license_access === "GRACE" || stats?.license_access === "READ_ONLY" || (daysLeft !== null && daysLeft <= 7)
        ? "warning"
        : "info";

  return (
    <Box sx={{ bgcolor: "#f9fafb", px: 4, py: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 0.5, gap: 2 }}>
        <Typography variant="h5" fontWeight={700}>{stats?.org_name || "Organisation"}</Typography>
        {showOnboard && (
          <Button
            variant="contained"
            startIcon={<AppsOutlinedIcon />}
            onClick={() => navigate("/home")}
            sx={{ textTransform: "none", bgcolor: "#0094FB", "&:hover": { bgcolor: "#0080e0" } }}
          >
            Go to Applications / Onboard
          </Button>
        )}
      </Box>
      <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
        <Chip label={stats?.tier?.charAt(0).toUpperCase() + stats?.tier?.slice(1)} size="small"
          sx={{ bgcolor: "#e3f2fd", color: "#1565c0", fontWeight: 700 }} />
        <Chip
          label={stats?.status?.charAt(0).toUpperCase() + stats?.status?.slice(1)}
          color={{ active: "success", suspended: "error", expired: "warning", pending: "default" }[stats?.status] || "default"}
          size="small" sx={{ fontWeight: 700 }}
        />
      </Box>

      {showExtensionBanner && (
        <Alert
          severity={extensionSeverity}
          sx={{ mb: 3, alignItems: "center" }}
          action={
            canRequestExtension ? (
              <Button
                color="inherit"
                size="small"
                startIcon={<ExtensionOutlinedIcon />}
                sx={{ textTransform: "none", fontWeight: 600, whiteSpace: "nowrap" }}
                onClick={() => navigate("/client/settings", { state: { focusContract: true } })}
              >
                Request Extension
              </Button>
            ) : null
          }
        >
          {contractExtensionBannerMessage(stats)}
          {canRequestExtension
            ? " Platform admin will receive an email to approve the extension."
            : " Contact your tenant admin to request an extension."}
        </Alert>
      )}

      {/* Seat usage banner */}
      <Card elevation={0} sx={{
        border: `1px solid ${overWarn ? "#ed6c02" : "#e8e8e8"}`,
        borderRadius: 2, p: 2.5, mb: 3,
        bgcolor: overWarn ? "#fff8f0" : "#fff",
      }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography fontWeight={600} fontSize="14px">
            Licence Usage — {usedPct}% used
          </Typography>
          <Typography fontSize="13px" color="text.secondary">
            {stats?.seats_used} / {stats?.seat_count} seats
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={usedPct}
          sx={{
            height: 8, borderRadius: 4,
            "& .MuiLinearProgress-bar": { bgcolor: overWarn ? "#ed6c02" : "#1976d2" },
          }}
        />
        {overWarn && (
          <Typography fontSize="12px" color="warning.main" mt={1}>
            Seat usage above {stats?.warning_threshold}% threshold.
          </Typography>
        )}
      </Card>

      {/* KPI cards */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} sm={3}>
          <KpiCard label="Total Seats" value={stats?.seat_count} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <KpiCard label="Seats Used" value={stats?.seats_used} color={overWarn ? "#ed6c02" : "#1976d2"} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <KpiCard
            label="Days to Expiry"
            value={daysLeft !== null ? (daysLeft < 0 ? "Expired" : daysLeft) : "—"}
            color={daysLeft !== null && daysLeft < 14 ? "#d32f2f" : undefined}
            sub={stats?.contract_end ? new Date(stats.contract_end).toLocaleDateString() : undefined}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <KpiCard
            label="Available Seats"
            value={stats?.seat_count != null && stats?.seats_used != null
              ? Math.max(0, stats.seat_count - stats.seats_used)
              : "—"}
            color="#2e7d32"
          />
        </Grid>
      </Grid>

      {/* Info card */}
      <Card elevation={0} sx={{ border: "1px solid #e8e8e8", borderRadius: 2, p: 3, bgcolor: "#fff", maxWidth: 480 }}>
        <Typography fontWeight={700} fontSize="14px" color="#344054" mb={1.5}>Organisation Info</Typography>
        {[
          ["Organisation", stats?.org_name],
          ["Tier", stats?.tier?.charAt(0).toUpperCase() + stats?.tier?.slice(1)],
          ["Status", stats?.status?.charAt(0).toUpperCase() + stats?.status?.slice(1)],
          ["Licence Access", stats?.license_access?.replace(/_/g, " ") || "—"],
          ["Contract End", stats?.contract_end ? new Date(stats.contract_end).toLocaleDateString() : "—"],
        ].map(([label, value]) => (
          <Box key={label} sx={{ display: "flex", justifyContent: "space-between", py: 0.75, borderBottom: "1px solid #f0f0f0" }}>
            <Typography fontSize="13px" color="text.secondary">{label}</Typography>
            <Typography fontSize="13px" fontWeight={500}>{value || "—"}</Typography>
          </Box>
        ))}
      </Card>
    </Box>
  );
}
